/* Five independent forearm tendon drives; one coupled curl coordinate per digit.
   Internal units: mm, radians, grams and newtons. Each actuator crosshead takes
   up one tendon while paying out an opposing tendon through a fixed heel idler.
   Joint coupling is an imposed mechanical constraint, not an outcome guaranteed
   by a loose tendon. Joint ratios/radii below are example design assumptions.

   theta_j = ratio_j * q; ds/dq = radius * sum(ratio_j).
   For each phalange COM and fingertip, J = d(position)/dq (mm/rad).
   Q_bound = sum(weight_N * |J_COM|) + tip_weight_N * |J_tip|.
   F_ideal = Q_bound / (ds/dq); F_design = F_ideal * 1.5 / 0.90.
   This is a conservative net actuator-force envelope over load directions,
   not fingertip grip capacity or either tendon's absolute pretensioned load.
   Both tendons require suitable pretension; neither cable can push. */
(function (root) {
  'use strict';
  const R = Math.PI / 180;
  const G = 9.80665;
  const FACTOR = 1.5;
  const EFFICIENCY = .90;
  const VERSION = '1.0.0';
  const DEFINITIONS = Object.freeze([
    Object.freeze({id: 'thumb', name: 'Thumb', ratios: Object.freeze([1, .9]), fractions: Object.freeze([.56, .44])}),
    ...['index', 'middle', 'ring', 'little'].map(id => Object.freeze({id, name: id[0].toUpperCase() + id.slice(1),
      ratios: Object.freeze([1, 1.15, .75]), fractions: Object.freeze([.5, .3, .2])}))
  ]);
  const DEFAULTS = Object.freeze({radiusMm: 5, fingers: Object.freeze([
    Object.freeze({id: 'thumb', name: 'Thumb', openAngle: 0, closedAngle: 55, massG: 18, tipLoadG: 100, lengthMm: 65}),
    Object.freeze({id: 'index', name: 'Index', openAngle: 0, closedAngle: 60, massG: 20, tipLoadG: 100, lengthMm: 90}),
    Object.freeze({id: 'middle', name: 'Middle', openAngle: 0, closedAngle: 60, massG: 22, tipLoadG: 100, lengthMm: 100}),
    Object.freeze({id: 'ring', name: 'Ring', openAngle: 0, closedAngle: 60, massG: 20, tipLoadG: 100, lengthMm: 95}),
    Object.freeze({id: 'little', name: 'Little', openAngle: 0, closedAngle: 60, massG: 16, tipLoadG: 100, lengthMm: 75})
  ])});
  const requireValue = (condition, message) => {if (!condition) throw new Error(message);};
  const sum = values => values.reduce((total, value) => total + value, 0);
  const magnitude = vector => Math.hypot(vector[0], vector[1]);
  function createDefaults() {return {radiusMm: DEFAULTS.radiusMm, fingers: DEFAULTS.fingers.map(f => ({...f}))};}
  function prepare(input) {
    const p = input ?? createDefaults();
    requireValue(typeof p.radiusMm === 'number' && Number.isFinite(p.radiusMm) && p.radiusMm >= 2 && p.radiusMm <= 10,
      'Use a tendon moment radius between 2 and 10 mm.');
    requireValue(Array.isArray(p.fingers) && p.fingers.length === 5, 'Enter exactly five digits, with one actuator for each.');
    const seen = new Set();
    const fingers = p.fingers.map(f => {
      requireValue(f && typeof f === 'object' && DEFINITIONS.some(d => d.id === f.id) && !seen.has(f.id),
        'Use each of the thumb, index, middle, ring and little finger once.');
      seen.add(f.id);
      const def = DEFINITIONS.find(d => d.id === f.id);
      const copy = {...f, name: def.name};
      for (const key of ['openAngle', 'closedAngle', 'massG', 'tipLoadG', 'lengthMm']) requireValue(typeof copy[key] === 'number' && Number.isFinite(copy[key]), 'Enter a finite number for ' + def.name + ' ' + key + '.');
      requireValue(copy.openAngle >= 0 && copy.openAngle <= copy.closedAngle && copy.closedAngle <= 80,
        def.name + ': use 0° ≤ open curl ≤ closed curl ≤ 80°.');
      requireValue(copy.massG >= 0 && copy.massG <= 1000 && copy.tipLoadG >= 0 && copy.tipLoadG <= 2000,
        def.name + ': moving mass must be 0–1000 g and supported fingertip load 0–2000 g.');
      requireValue(copy.lengthMm >= 30 && copy.lengthMm <= 150, def.name + ': use a total digit length between 30 and 150 mm.');
      return {...copy, definition: def};
    });
    return {radiusMm: p.radiusMm, fingers};
  }
  function fingerPose(prepared, index, t) {
    requireValue(Number.isInteger(index) && index >= 0 && index < 5, 'Choose a finger index from 0 to 4.');
    requireValue(typeof t === 'number' && Number.isFinite(t) && t >= 0 && t <= 1, 'Curl travel must be between 0 and 1.');
    const f = prepared.fingers[index], {ratios, fractions} = f.definition;
    const angleDeg = f.openAngle + (f.closedAngle - f.openAngle) * t;
    const angleRad = angleDeg * R;
    const jointAnglesRad = ratios.map(r => angleRad * r);
    const jointAnglesDeg = ratios.map(r => angleDeg * r);
    const lengths = fractions.map(part => part * f.lengthMm);
    const massesG = fractions.map(part => part * f.massG);
    const joints = [[0, 0]], centers = [], jointJacobians = [[0, 0]], centerJacobians = [];
    let absoluteAngle = 0, cumulativeRatio = 0;
    for (let j = 0; j < ratios.length; j++) {
      absoluteAngle += jointAnglesRad[j];
      cumulativeRatio += ratios[j];
      const delta = [lengths[j] * Math.cos(absoluteAngle), lengths[j] * Math.sin(absoluteAngle)];
      const derivative = [-lengths[j] * cumulativeRatio * Math.sin(absoluteAngle), lengths[j] * cumulativeRatio * Math.cos(absoluteAngle)];
      const start = joints[j], startJ = jointJacobians[j];
      centers.push([start[0] + delta[0] / 2, start[1] + delta[1] / 2]);
      centerJacobians.push([startJ[0] + derivative[0] / 2, startJ[1] + derivative[1] / 2]);
      joints.push([start[0] + delta[0], start[1] + delta[1]]);
      jointJacobians.push([startJ[0] + derivative[0], startJ[1] + derivative[1]]);
    }
    const tip = joints[joints.length - 1].slice();
    const tipJacobian = jointJacobians[jointJacobians.length - 1].slice();
    const tendonJacobianMm = prepared.radiusMm * sum(ratios);
    const tendonExcursionMm = tendonJacobianMm * angleRad;
    const signedExcursionMm = tendonJacobianMm * (angleDeg - f.openAngle) * R;
    const strokeMm = tendonJacobianMm * (f.closedAngle - f.openAngle) * R;
    const tipGeneralizedLoadNmm = f.tipLoadG / 1000 * G * magnitude(tipJacobian);
    const movingGeneralizedLoadNmm = sum(centerJacobians.map((j, i) => massesG[i] / 1000 * G * magnitude(j)));
    const generalizedLoadNmm = tipGeneralizedLoadNmm + movingGeneralizedLoadNmm;
    const idealForceN = generalizedLoadNmm / tendonJacobianMm;
    return {
      id: f.id, name: f.name, index, t, angleDeg, angleRad,
      ratios: ratios.slice(), jointAnglesRad, jointAnglesDeg,
      lengths, massesG, joints, points: joints, centers, tip,
      jointJacobians, centerJacobians, tipJacobian,
      jacobians: {joints: jointJacobians, centers: centerJacobians, tip: tipJacobian},
      tendonJacobianMm, tendonExcursionMm, signedExcursionMm,
      travelMm: signedExcursionMm, strokeMm,
      flexorTakeupMm: signedExcursionMm,
      extensorTakeupMm: -signedExcursionMm,
      tipGeneralizedLoadNmm, movingGeneralizedLoadNmm, generalizedLoadNmm,
      idealForceN, forceN: idealForceN * FACTOR / EFFICIENCY,
      pushEnvelopeN: idealForceN * FACTOR / EFFICIENCY,
      pullEnvelopeN: idealForceN * FACTOR / EFFICIENCY
    };
  }
  function pose(parameters, index, t) {return fingerPose(prepare(parameters), index, t);}
  function solve(parameters) {
    const prepared = prepare(parameters);
    const fingers = prepared.fingers.map((f, index) => {
      const samples = Array.from({length: 241}, (_, i) => fingerPose(prepared, index, i / 240));
      const peak = samples.reduce((best, s) => s.forceN > best.forceN ? s : best);
      return {id: f.id, name: f.name, index, jointCount: f.definition.ratios.length,
        strokeMm: samples[0].strokeMm, peakN: peak.forceN, peakAt: peak.t,
        idealPeakN: peak.idealForceN, maxPushN: peak.forceN, maxPullN: peak.forceN,
        open: samples[0], closed: samples[samples.length - 1], samples};
    });
    return {
      version: VERSION,
      parameters: {radiusMm: prepared.radiusMm, fingers: prepared.fingers.map(({definition, ...f}) => f)},
      fingers, maxPeakN: Math.max(...fingers.map(f => f.peakN)),
      maxStrokeMm: Math.max(...fingers.map(f => f.strokeMm)), totalActuators: 5,
      fixedAssumptions: {designAllowance: FACTOR, mechanicalEfficiency: EFFICIENCY,
        loadEnvelope: 'Conservative weight-load direction envelope',
        coupling: 'Fixed joint ratios, one curl coordinate per digit',
        return: 'Opposing tendon pair with equal effective moment radii',
        pretensionIncluded: false, contactGripCapacityCalculated: false, collisionChecked: false}
    };
  }
  const api = {version: VERSION, defaults: DEFAULTS, definitions: DEFINITIONS, createDefaults,
    constants: Object.freeze({gravityMPerS2: G, designAllowance: FACTOR, mechanicalEfficiency: EFFICIENCY}), pose, solve};
  root.FGRobotHandModel = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
